<script>
	// NPM Modules
	import { tick } from 'svelte';
	import cloneDeep from 'lodash/cloneDeep.js';
	// Stores
	import UserStore from '@/Stores/UserStore.js';
	import ACLStore from '@/Stores/ACLStore.js';
	// Components
	import Modal from '@/Components/Modal.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import FontIcon from '@/Components/FontIcon.svelte';
	import Editable from '@/Components/Editable.svelte';
	import Icon from '@/Components/Icon.svelte';
	import TextInput from '@/Components/NewTextInput.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	import Prompt from '@/Components/Prompt.svelte';
	// Constants
	import { BetCategory } from '@/Constants/Bet.js';
	import { RewardType } from '@/Constants/Promotions.js';
	// Services
	import { notify } from '@/Services/Notify.js';
	import { CurrencyFormat, NumberFormat, NumberTransform, JurisdictionHelper } from '@/Services/Helpers.js';
	// Models
	import Package from '@/Models/Package.js';

	export let user;

	const iconMap = {
		[BetCategory.Racing]: 'gallop',
		[BetCategory.Sport]: 'afl'
	};

	let errors = null;
	let sending = false;
	let openPackageSetting = false;
	let openCustomPackageCreator = false;
	let userPackageSelected = false;
	let showRemovePackagePrompt = false;

	$: customPackages = [];

	$: boosts =
		user && $UserStore && loadBoosts(UserStore.getUserRewards()?.firstWhere('type.id', RewardType.Boost) ?? []);
	$: promotionRewards =
		user &&
		$UserStore &&
		loadPromotionRewards(UserStore.getUserRewards()?.firstWhere('type.id', RewardType.Promotion) ?? []);
	$: availablePackages = [];

	function loadCustomPackageDefaults(fillFromCurrent = true, zeroValues = false) {
		let customPackage = [];
		for (const rewardPackages of UserStore.getUserRewards()) {
			for (const rewardType of Object.keys(rewardPackages.rewards)) {
				const rewardPackage = {
					reward_type_id: parseInt(rewardPackages.type.id),
					reward_name: rewardPackages.rewards[rewardType],
					reward_id: parseInt(rewardType),
					allocation: zeroValues ? 0 : undefined
				};

				const existingSettings = getPackageSettingsByIdAndType(rewardPackage.reward_type_id, rewardPackage.reward_id);
				if (rewardPackage.reward_type_id == RewardType.Boost) {
					rewardPackage['max_bet_amount'] = zeroValues ? 0 : undefined;
					if (fillFromCurrent && existingSettings) {
						rewardPackage['allocation'] =
							existingSettings['allocation'] == 0 && existingSettings['max_bet_amount'] == 0
								? undefined
								: existingSettings['allocation'];
						rewardPackage['max_bet_amount'] =
							existingSettings['allocation'] == 0 && existingSettings['max_bet_amount'] == 0
								? undefined
								: existingSettings['max_bet_amount'];
					}
				} else if (rewardPackage.reward_type_id == RewardType.Promotion) {
					rewardPackage['max_bonus_amount'] = zeroValues ? 0 : undefined;
					if (fillFromCurrent && existingSettings) {
						rewardPackage['allocation'] =
							existingSettings['allocation'] == 0 && existingSettings['max_bonus_amount'] == 0
								? undefined
								: existingSettings['allocation'];
						rewardPackage['max_bonus_amount'] =
							existingSettings['allocation'] == 0 && existingSettings['max_bonus_amount'] == 0
								? undefined
								: existingSettings['max_bonus_amount'];
					}
				}

				customPackage.push(rewardPackage);
			}
		}
		customPackages = customPackage;
	}

	function loadBoosts(boostRewards) {
		if (!boostRewards.rewards) {
			return [];
		}

		const boosts = [];
		for (const rewardId of Object.keys(boostRewards.rewards)) {
			const boostToken = {
				name: boostRewards.rewards[rewardId],
				settings: getPackageSettingsByIdAndType(RewardType.Boost, rewardId),
				tokens: getPackageTokenByIdAndType(RewardType.Boost, rewardId)
			};
			boosts.push(boostToken);
		}

		return boosts;
	}

	function loadPromotionRewards(promotionRewards) {
		if (!promotionRewards.rewards) {
			return [];
		}
		const promotions = [];
		for (const rewardId of Object.keys(promotionRewards.rewards)) {
			const promotionsToken = {
				name: promotionRewards.rewards[rewardId],
				settings: getPackageSettingsByIdAndType(RewardType.Promotion, rewardId),
				tokens: getPackageTokenByIdAndType(RewardType.Promotion, rewardId)
			};
			promotions.push(promotionsToken);
		}

		return promotions;
	}

	function getPackageTokenByIdAndType(rewardTypeId, rewardId) {
		if (!user.package_tokens) {
			return false;
		}

		return user?.package_tokens?.data?.firstWhere('id', user.id + '-' + rewardTypeId + '-' + rewardId);
	}

	function getPackageSettingsByIdAndType(rewardTypeId, rewardId) {
		if (!user.package) {
			return false;
		}

		return user.package?.settings?.data?.firstWhere('id', user.package.id + '-' + rewardTypeId + '-' + rewardId);
	}

	async function onSaveCount(reward_type_id, reward_id, active_count) {
		sending = true;
		user
			.updatePackageCount(reward_type_id, reward_id, active_count)
			.then(() => {
				notify({
					type: 'success',
					title: user.name,
					message: 'Reward Count updated',
					timeout: 3
				});
				tick();
				UserStore.getUserWithDetails(user.id);
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to update Reward Count',
					timeout: 5
				});
			})
			.finally(() => (sending = false));
	}

	async function openPackageSettings() {
		userPackageSelected = user.package.id;
		await getUserPackages();
		await tick();
		openPackageSetting = true;
		window.scrollTo(0, 0);
	}

	async function openCustomPackageCreatorModal() {
		loadCustomPackageDefaults();
		openCustomPackageCreator = !openCustomPackageCreator;
		await tick();
		window.scrollTo(0, 0);
	}

	async function openRemovePackagePrompt() {
		showRemovePackagePrompt = !showRemovePackagePrompt;
		await tick();
		window.scrollTo(0, 0);
	}

	async function removeUserPackage() {
		loadCustomPackageDefaults(false, true);
		const newCustomPackage = cloneDeep(customPackages);
		for (const rewardPackage in newCustomPackage) {
			delete newCustomPackage[rewardPackage]['reward_name'];
		}
		let userPackage = new Package();
		sending = true;
		userPackage
			.createCustomUserPackages(user.id, newCustomPackage)
			.then(() => {
				notify({
					type: 'success',
					title: user.name,
					message: 'User package successfully removed',
					timeout: 3
				});
				tick();
				UserStore.getUserWithDetails(user.id);
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to remove user package',
					timeout: 5
				});
			})
			.finally(() => (sending = false));
	}

	async function getUserPackages() {
		const packages = new Package();
		packages.getUserPackages(user.id).then((userPackages) => {
			availablePackages = userPackages.data.all();
		});
	}

	async function saveUserPackage() {
		let userPackage = new Package();
		await tick();
		sending = true;
		userPackage
			.setUserPackages(user.id, userPackageSelected)
			.then(() => {
				notify({
					type: 'success',
					title: user.name,
					message: 'User Package updated',
					timeout: 3
				});
				UserStore.getUserWithDetails(user.id);
				openPackageSetting = false;
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to update User Package',
					timeout: 5
				});
			})
			.finally(() => (sending = false));
	}

	async function saveCustomUserPackage() {
		const newCustomPackage = cloneDeep(customPackages);
		errors = {};
		let errorsExist = false;
		for (const rewardPackage in newCustomPackage) {
			delete newCustomPackage[rewardPackage]['reward_name'];
			if (newCustomPackage[rewardPackage]['allocation'] == undefined) {
				errors = Object.assign(errors, {
					['package_settings.' + rewardPackage + '.allocation']: [
						'Allocation amount is required to be set for all reward types'
					]
				});
				errorsExist = true;
			}

			if (
				Object.hasOwn(newCustomPackage[rewardPackage], 'max_bet_amount') &&
				newCustomPackage[rewardPackage]['max_bet_amount'] == undefined
			) {
				errors = Object.assign(errors, {
					['package_settings.' + rewardPackage + '.max_bet_amount']: [
						'Max bet amount is required to be set for all reward types'
					]
				});
				errorsExist = true;
			}
			if (
				Object.hasOwn(newCustomPackage[rewardPackage], 'max_bonus_amount') &&
				newCustomPackage[rewardPackage]['max_bonus_amount'] == undefined
			) {
				errors = Object.assign(errors, {
					['package_settings.' + rewardPackage + '.max_bonus_amount']: [
						'Max return amount is required to be set for all reward types'
					]
				});
				errorsExist = true;
			}
		}
		if (errorsExist) {
			return;
		}
		let userPackage = new Package();
		sending = true;
		userPackage
			.createCustomUserPackages(user.id, newCustomPackage)
			.then(() => {
				notify({
					type: 'success',
					title: user.name,
					message: 'Custom user package successfully created',
					timeout: 3
				});
				tick();
				UserStore.getUserWithDetails(user.id);
				openCustomPackageCreator = false;
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to create custom user package',
					timeout: 5
				});
			})
			.finally(() => (sending = false));
	}
</script>

<Prompt
	bind:open={showRemovePackagePrompt}
	title="Confirm clearing users package!"
	message="This will clear the package from the user which mean they will not get any tokens in the next cycle, but will not clear their current tokens!"
	onConfirm={() => removeUserPackage()}
/>

<div class="flex flex-col md:w-1/2 text-dark-grey bg-gray-50 min-h-48">
	<div class="flex items-center bg-dark-grey text-light justify-between">
		<div class="flex flex-row w-full">
			{#if $ACLStore && ACLStore.granted('package.update', true) && !JurisdictionHelper().isSocial()}
				<LoadingButton
					class="btn btn-primary my-1 flex flex-shrink-0 justify-center items-center ml-1 md:ml-2 px-2 py-1.5"
					on:click={openPackageSettings}
				>
					<span class="flex flex-row justify-center items-center text-wrap md:text-nowrap w-12 md:w-auto">
						<Icon name="gear" classes="w-4 h-4 md:mr-2 hidden md:block" />
						Change Package
					</span>
				</LoadingButton>
			{/if}
			<div class="md:hidden flex flex-col items-center justify-center flex-grow w-full">
				<h2 class="ml-2 flex-1 py-1 text-lg italic font-sans-condensed items-center flex-grow">Selected Package</h2>
				<h2 class="ml-2 flex-1 py-1 text-lg italic font-sans-condensed items-center flex-grow">
					{user?.package?.package_type?.name ?? 'No package selected'}
				</h2>
			</div>
			<h2 class="hidden md:block ml-2 flex-1 py-2 text-xl italic font-sans-condensed items-center flex-grow">
				Selected Package - {user?.package?.package_type?.name ?? 'No package selected'}
			</h2>
		</div>

		{#if $ACLStore && ACLStore.granted('package.update', true)}
			<div class="flex flex-row mr-0 md:mr-2">
				<LoadingButton
					class="btn btn-primary-action my-1 flex flex-shrink-0 justify-center items-center mx-0 md:mx-2 px-2 py-1.5"
					on:click={openCustomPackageCreatorModal}
				>
					<span class="flex flex-row justify-center items-center text-wrap md:text-nowrap w-12 md:w-auto">
						Make Custom Package
					</span>
				</LoadingButton>
				{#if !JurisdictionHelper().isSocial()}
					<LoadingButton
						class="btn btn-danger my-1 flex flex-shrink-0 justify-center items-center mx-1 md:mx-2 px-2 py-1.5"
						on:click={openRemovePackagePrompt}
					>
						<span class="flex flex-row justify-center items-center text-wrap md:text-nowrap w-16 md:w-auto">
							<span class="mt-0.5">Clear Package</span>
							<Icon name="bin" classes="w-4 h-4 md:ml-2 hidden md:block" />
						</span>
					</LoadingButton>
				{/if}
			</div>
		{/if}
	</div>
	<div class="flex flex-col w-full text-center justify-center overflow-auto">
		<table class:opacity-50={sending}>
			<thead>
				<tr class="bg-dark-grey">
					<th colspan="7" class="text-light text-md italic font-sans-condensed font-medium text-left py-1 px-2"
						>Boost
						<FontIcon icon="boost-up" /></th
					>
				</tr>
				<tr class="text-sm text-center bg-grey text-light border-0">
					<th class="text-left px-2 py-0.5 font-medium">Description</th>
					<th class="px-4 py-0.5 font-medium">Daily Allocation</th>
					<th class="px-4 py-0.5 font-medium">Max Stake</th>
					<th class="px-4 py-0.5 font-medium">Balance</th>
					<th class="px-4 py-0.5 font-medium">Lifetime Awarded</th>
					<th class="px-4 py-0.5 font-medium">Lifetime Used</th>
					<th class="px-4 py-0.5 font-medium">% Used</th>
				</tr>
			</thead>
			{#if boosts.length == 0}
				<tbody>
					<tr>
						<td colspan="7">No Boost Up's available</td>
					</tr>
				</tbody>
			{:else}
				<tbody>
					{#each boosts as boost}
						<tr class="text-center">
							<td class="px-2 py-0.5 text-left border-b font-semibold text-nowrap">
								{#if boost.name.includes('Racing')}
									<FontIcon icon={iconMap[BetCategory.Racing]} className="mr-1" />
								{:else}
									<FontIcon icon={iconMap[BetCategory.Sport]} className="mr-1" />
								{/if}
								{boost.name}
							</td>
							<td class="px-4 py-0.5 border-b">
								<!-- This can be blank if they are on like boost only package or bonus only package-->
								{boost?.settings?.allocation ?? 0}
							</td>
							<td class="px-4 py-0.5 border-b border-r">
								<!-- This can be blank if they are on like boost only package or bonus only package-->
								{CurrencyFormat(boost?.settings?.max_bet_amount ?? 0)}
							</td>
							<td class="px-4 py-0.5 border-b border-r">
								{#if $ACLStore && ACLStore.granted('package.update')}
									<Editable
										onClick={(active_count) =>
											onSaveCount(boost.tokens.reward.type.id, boost.tokens.reward.id, parseInt(active_count))}
										text={boost?.tokens?.active_count ?? 0}
										formInputOnEdit={true}
									/>
								{:else}{boost?.tokens?.active_count ?? 0}{/if}
							</td>
							<td class="px-4 py-0.5 border-b">
								{boost?.tokens?.awarded_count ? NumberFormat(boost.tokens.awarded_count, true) : '-'}
							</td>
							<td class="px-4 py-0.5 border-b">
								{boost?.tokens?.spent_count
									? NumberFormat(boost.tokens.spent_count, true)
									: boost?.tokens?.awarded_count > 0
										? '0'
										: '-'}
							</td>
							<td class="px-4 py-0.5 border-b">
								{boost?.tokens?.awarded_count && boost?.tokens?.awarded_count > 0
									? ((boost?.tokens?.spent_count / boost?.tokens?.awarded_count) * 100).toFixed(0) + '%'
									: '-'}
							</td>
						</tr>
					{/each}
					<tr>
						<td colspan="7">
							<p class="px-2 italic text-right text-tiny">Boost Up's expire 24 hours after allocation</p>
						</td>
					</tr>
				</tbody>
			{/if}

			<thead>
				<tr class="bg-dark-grey">
					<th colspan="7" class="text-light text-md italic font-sans-condensed font-medium text-left py-1 px-2">
						<div class="space-x-1">
							<span>Bet Backs</span>
							<FontIcon icon="bonus-back" />
						</div>
					</th>
				</tr>
				<tr class="text-sm text-center bg-grey text-light">
					<th class="text-left px-2 py-0.5 font-medium">Description</th>
					<th class="px-4 py-0.5 font-medium">Daily Allocation</th>
					<th class="px-4 py-0.5 font-medium">Max Return</th>
					<th class="px-4 py-0.5 font-medium">Balance</th>
					<th class="px-4 py-0.5 font-medium">Lifetime Awarded</th>
					<th class="px-4 py-0.5 font-medium">Lifetime Used</th>
					<th class="px-4 py-0.5 font-medium">% Used</th>
				</tr>
			</thead>
			{#if promotionRewards.length == 0}
				<tbody>
					<tr>
						<td colspan="7">No Bet Back's available</td>
					</tr>
				</tbody>
			{:else}
				<tbody>
					{#each promotionRewards as reward}
						<tr class="text-center">
							<td class="px-2 py-0.5 text-left border-b font-semibold text-nowrap">
								{#if reward.name.includes('Racing') || reward.name.includes('SRM')}
									<FontIcon icon={iconMap[BetCategory.Racing]} className="mr-1" />
								{:else}
									<FontIcon icon={iconMap[BetCategory.Sport]} className="mr-1" />
								{/if}
								{reward.name}
							</td>
							<td class="px-4 py-0.5 border-b">
								<!-- This can be blank if they are on like boost only package or bonus only package-->
								{reward?.settings?.allocation ?? '-'}
							</td>
							<td class="px-4 py-0.5 border-b border-r">
								<!-- This can be blank if they are on like boost only package or bonus only package-->
								{reward?.settings?.max_bonus_amount ? CurrencyFormat(reward?.settings?.max_bonus_amount) : '-'}
							</td>
							<td class="px-4 py-0.5 border-b border-r">
								{#if $ACLStore && ACLStore.granted('package.update')}
									<Editable
										onClick={(active_count) =>
											onSaveCount(reward.tokens.reward.type.id, reward.tokens.reward.id, parseInt(active_count))}
										text={reward?.tokens?.active_count ?? '-'}
										formInputOnEdit={true}
									/>
								{:else}{reward?.tokens?.active_count ?? '-'}{/if}
							</td>
							<td class="px-4 py-0.5 border-b">
								{reward?.tokens?.awarded_count ? NumberFormat(reward?.tokens?.awarded_count, true) : '-'}
							</td>
							<td class="px-4 py-0.5 border-b">
								{reward?.tokens?.spent_count
									? NumberFormat(reward?.tokens?.spent_count, true)
									: reward?.tokens?.awarded_count > 0
										? '0'
										: '-'}
							</td>
							<td class="px-4 py-0.5 border-b">
								{reward?.tokens?.awarded_count && reward?.tokens?.awarded_count > 0
									? ((reward?.tokens?.spent_count / reward?.tokens?.awarded_count) * 100).toFixed(0) + '%'
									: '-'}
							</td>
						</tr>
					{/each}
					<tr>
						<td colspan="7">
							<p class="px-2 italic text-right text-tiny">Bet Backs expire 24 hours after allocation</p>
						</td>
					</tr>
				</tbody>
			{/if}
		</table>
	</div>
</div>

{#if openPackageSetting}
	<Modal on:close={() => (openPackageSetting = false)} className="mx-2 md:w-1/3 md:mx-auto">
		<div class="pt-2 font-semibold text-center uppercase" slot="header">
			Package setting for {user.full_name}
		</div>

		<div class="flex flex-row w-full px-4">
			<SelectInput
				name="package_id"
				label="Package"
				className="pb-4 w-full"
				required={true}
				errors={errors && errors['package_id']}
				on:change={(event) => {
					userPackageSelected = event.target.value;
				}}
			>
				{#if user.package.is_custom}
					<option value={false} disabled selected>Custom - A Custom Package</option>
				{:else}
					<option value={false} disabled selected>Select Package...</option>
				{/if}

				{#each availablePackages as rewardPackage (rewardPackage.id)}
					<option value={rewardPackage.id} selected={userPackageSelected == rewardPackage.id}
						>{rewardPackage.package_type.name} - {rewardPackage.description}</option
					>
				{/each}
			</SelectInput>
		</div>
		<div class="flex flex-col w-full px-4 pt-2 text-center justify-center">
			<LoadingButton on:click={saveUserPackage} loading={sending} className="mb-4 btn btn-primary">
				Update package
			</LoadingButton>
			<LoadingButton class="mb-4 btn btn-muted" on:click={() => (openPackageSetting = false)}>Cancel</LoadingButton>
		</div>
	</Modal>
{/if}

{#if openCustomPackageCreator}
	<Modal on:close={() => (openCustomPackageCreator = false)} className="mx-2 md:w-1/3 md:mx-auto">
		<div class="pt-2 font-semibold text-center uppercase" slot="header">
			Custom package for {user.full_name}
		</div>
		<div class="flex flex-col w-full px-4 pt-2 text-left justify-center">
			{#each customPackages as customPackage, packageIndex}
				<h2 class="mt-2 text-lg font-bold">{customPackage.reward_name}</h2>
				<div class="mt-1 flex flex-row w-full space-x-4">
					<TextInput
						name="allocation"
						label="Daily Allocation"
						type="number"
						className="pb-4 w-1/2"
						required={true}
						bind:value={customPackage.allocation}
						min="1"
						step="1"
						placeholder="Daily Allocation"
						errors={errors && errors['package_settings.' + packageIndex + '.allocation']}
					/>
					{#if customPackage.reward_type_id == RewardType.Boost}
						<TextInput
							name="max_bet_amount"
							label="Max Bet Amount"
							type="number"
							className="pb-4 w-1/2"
							required={true}
							prefix="$"
							step="0.01"
							min="1"
							placeholder="Max Bet Amount"
							errors={errors && errors['package_settings.' + packageIndex + '.max_bet_amount']}
							on:change={(event) => (customPackage.max_bet_amount = Math.round(parseFloat(event.target.value)) * 100)}
							on:keyup={(event) => {
								if (event.keyCode === 13) {
									customPackage.max_bet_amount = Math.round(parseFloat(event.target.value)) * 100;
								}
							}}
							value={NumberTransform(customPackage.max_bet_amount, false)}
						/>
					{:else if customPackage.reward_type_id == RewardType.Promotion}
						<TextInput
							name="max_bonus_amount"
							label="Max Return Amount"
							type="number"
							className="pb-4 w-1/2"
							required={true}
							prefix="$"
							step="0.01"
							min="1"
							placeholder="Max Return Amount"
							errors={errors && errors['package_settings.' + packageIndex + '.max_bonus_amount']}
							on:change={(event) => (customPackage.max_bonus_amount = Math.round(parseFloat(event.target.value)) * 100)}
							on:keyup={(event) => {
								if (event.keyCode === 13) {
									customPackage.max_bonus_amount = Math.round(parseFloat(event.target.value)) * 100;
								}
							}}
							value={NumberTransform(customPackage.max_bonus_amount, false)}
						/>
					{/if}
				</div>
				<hr />
			{/each}
		</div>
		<div class="flex flex-col w-full px-4 pt-2 text-center justify-center">
			<LoadingButton on:click={saveCustomUserPackage} loading={sending} className="mb-4 btn btn-primary">
				Create custom package
			</LoadingButton>
			<LoadingButton class="mb-4 btn btn-muted" on:click={() => (openCustomPackageCreator = false)}
				>Cancel</LoadingButton
			>
		</div>
	</Modal>
{/if}
